import {
    DeleteOutlineOutlined,
    EditOutlined,
    ReplayOutlined,
    ViewColumn,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    CSSProperties,
    Dispatch,
    Fragment,
    ReactElement,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "../../Utilities/AppContext";
import { DELETE, POST } from "../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../Utilities/MultiPermissionAuthorize";
import { Print } from "../../Utilities/Print";
import { TableToExcel } from "../../Utilities/TableToExcel";
import { Footer } from "../Header/Footer";
import { Header } from "../Header/Header";
import { BlindPagination } from "./Components/BlindPagination";
import SearchBar from "./Components/SearchBar";

type ComplexHeaders = {
    label: ReactNode;
    styles: CSSProperties;
    props: any;
};

type Props = {
    headers: any;
    data: any[];
    delEndPoint: string;
    feedback?: string;
    setFeedback?: Dispatch<SetStateAction<any>>;
    pagination: { page: any; limit: any };
    setPagination: Dispatch<SetStateAction<any>>;
    rowsCount: number;
    isLoading: boolean;
    permissions: { edit: string[]; delete: string[] };
    reportName: string;
    queryKey: any[];
    defaultSelectedHeaders: any[];
    blindPagination?: boolean;
    reportTemplates?: any[];
    reportCategory?: string;
    hasReportTemplates?: boolean;
    width?: number;
    load?: boolean;
    search?: any;
    setSearch?: Dispatch<SetStateAction<any>>;
    _printables?: string[];
    features?: { download: ReactNode };
    reportDetails?: { [key: string]: { label: string; value: any } };
    deletionProp?: any;
    summaryRow?: (cols: string[]) => any[];
    showPagination?: any;
    showSelection?: boolean | string[];
    itemTypeName?: string;
};

const addReportTemplate = (template: {
    key: string;
    value: string;
    user: number;
    category: string;
}) => POST("/preferences", template);

const BaseTable = ({
    headers,
    data,
    delEndPoint,
    feedback,
    setFeedback,
    rowsCount,
    pagination,
    setPagination,
    isLoading,
    permissions,
    reportName = "",
    queryKey,
    defaultSelectedHeaders = [],
    blindPagination = false,
    reportTemplates = [],
    reportCategory,
    hasReportTemplates = false,
    width = 1024,
    load = false,
    search,
    setSearch,
    _printables = [],
    features,
    reportDetails,
    deletionProp = "id",
    showPagination = true,
    summaryRow,
    showSelection = false,
    itemTypeName = "reading",
}: Props) => {
    const [open, setOpen] = useState(false);
    const [id, setId] = useState("");
    const [printMode, setPrintMode] = useState(false);
    const [printables, setPrintables] = useState(_printables);
    const [openAddReportTemplate, setOpenAddReportTemplate] = useState(false);
    const [deletionResponse, setDeletionResponse] = useState("");
    const [openDeletionFeedback, setOpenDeletionFeedback] = useState(false);
    const [reportTemplateFeedback, setReportTemplateFeedback] = useState({
        show: false,
        loading: false,
        message: "",
        status: "",
    });
    const [selectedHeaders, setSelectedHeaders] = useState<any>(
        defaultSelectedHeaders
    );
    const [selectedReportTemplate, setSelectedReportTemplate] = useState<{
        name: string;
        columns: string[];
    }>({
        name: "",
        columns: [],
    });
    const [newReportTemplate, setNewReportTemplate] = useState({
        key: "",
        value: [],
    });
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState(false);
    const [confirmationText, setConfirmationText] = useState("");

    const { user } = useContext(AppContext);

    const client = useQueryClient();
    const printRef = useRef<any>();
    const navigate = useNavigate();
    const history = useLocation();
    const qc = useQueryClient();

    const name = history.pathname.split("/");

    // Determine if selection is enabled (either boolean or permission-based)
    const selectionOps = Array.isArray(showSelection) ? showSelection : [];
    const isSelectionEnabled = Array.isArray(showSelection)
        ? Array.isArray(user?.ops) &&
          showSelection.some((op) => user.ops.includes(op))
        : showSelection;

    const handleEdit = (item: any) => {
        const updatedUrl = history.pathname + "/update?id=" + item.id;
        navigate(updatedUrl);
    };

    const handleDelete = (id: string) => {
        setId(id);
        setOpen(true);
    };

    const deleteSoft = () => {
        return DELETE(delEndPoint + `/soft?id=${id}`);
    };

    const delMutation = useMutation(deleteSoft, {
        onSuccess(res) {
            setDeletionResponse(res.data.message);
            setOpenDeletionFeedback(true);
            client.invalidateQueries(queryKey);
            setTimeout(() => {
                setDeletionResponse("");
                setOpenDeletionFeedback(false);
            }, 3000);
        },
        onError(err: any) {
            console.log(err.response.data.message);
            setDeletionResponse(err.response.data.message);
            setOpenDeletionFeedback(true);
        },
    });

    const deleteSingleSoft = async (id: string): Promise<any> => {
        try {
            const response =
                itemTypeName === "collection"
                    ? await DELETE(`${delEndPoint}?ids[]=${id}`)
                    : await DELETE(`${delEndPoint}/soft?id=${id}`);
            return { success: true, id, message: response?.data.message };
        } catch (err: any) {
            return {
                success: false,
                id,
                message:
                    err.response?.data?.message ||
                    `Failed to delete item ${id}`,
            };
        }
    };

    const bulkDelMutation = useMutation(
        async (ids: string[]) => {
            const results = await Promise.all(
                ids.map((id) => deleteSingleSoft(id))
            );
            return results;
        },
        {
            onSuccess(results) {
                const successCount = results.filter((r) => r.success).length;
                const failureCount = results.filter((r) => !r.success).length;

                // Build detailed message for each item
                let message = "";
                if (successCount > 0) {
                    message += `Successfully deleted ${successCount} ${itemTypeName == "collection" ? "Collection" : "Reading"}(s):\n`;
                    results
                        .filter((r) => r.success)
                        .forEach((r) => {
                            message += `✓  ${itemTypeName == "collection" ? "Collection Id" : "Reading Id"} ${r.id}: ${r.message}\n`;
                        });
                }
                if (failureCount > 0) {
                    message += `\nFailed to delete ${failureCount} ${itemTypeName == "collection" ? "Collection Id" : "Readings Id"}(s):\n`;
                    results
                        .filter((r) => !r.success)
                        .forEach((r) => {
                            message += `✗ ${itemTypeName == "collection" ? "Collection Id" : "Readings Id"} ${r.id}: ${r.message}\n`;
                        });
                }

                setDeletionResponse(message);
                setOpenDeletionFeedback(true);
                setSelectedRows(new Set());
                setConfirmationText("");
                setOpenBulkDeleteDialog(false);
                client.invalidateQueries(queryKey);
                setTimeout(() => {
                    setDeletionResponse("");
                    setOpenDeletionFeedback(false);
                }, 8000);
            },
            onError(err: any) {
                console.log(err.response.data.message);
                setDeletionResponse(
                    `Bulk delete failed: ${err.response?.data?.message || err.message}`
                );
                setOpenDeletionFeedback(true);
            },
        }
    );

    const next = () => {
        delMutation.mutate();
    };

    const handleSelectRow = useCallback((rowId: string) => {
        setSelectedRows((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        if (selectedRows.size === data?.length) {
            setSelectedRows(new Set());
        } else {
            setSelectedRows(
                new Set(data?.map((item: any) => item[deletionProp]))
            );
        }
    }, [selectedRows.size, data?.length, data, deletionProp]);

    const handleBulkDelete = () => {
        if (selectedRows.size === 0) return;
        setOpenBulkDeleteDialog(true);
        setConfirmationText("");
    };

    const confirmBulkDelete = () => {
        if (confirmationText.toLowerCase() === "delete all") {
            const idsArray = Array.from(selectedRows);
            bulkDelMutation.mutate(idsArray);
        }
    };

    const handlePageChange = (ev: any, page: number) => {
        setPagination({ ...pagination, page: page });
    };

    const handleRowsPerPageChange = (ev: any) => {
        setPagination({ ...pagination, limit: ev.target.value });
    };

    useEffect(() => {
        if (printMode) {
            if (_printables.length > 0) {
                const targetPrintables = selectedHeaders.filter((col: string) =>
                    _printables.includes(col)
                );

                setPrintables(targetPrintables);
            } else {
                setPrintables(selectedHeaders);
            }
        }
    }, [printMode, selectedHeaders]);

    // Merge the virtual selection column into headers for column visibility
    const headersWithSelection = useMemo(() => {
        if (!isSelectionEnabled) return headers;
        return { __selection__: "Selection", ...headers };
    }, [headers, isSelectionEnabled]);

    // Filter out __selection__ from selectedHeaders for rendering data columns
    const dataHeaders = useMemo(
        () => selectedHeaders.filter((col: string) => col !== "__selection__"),
        [selectedHeaders]
    );

    // Check if selection column is visible
    const isSelectionVisible =
        isSelectionEnabled && selectedHeaders.includes("__selection__");

    return load ? (
        <Box sx={{ height: "100%" }}>
            <Grid
                container
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
            >
                <Grid item xs={12} md="auto">
                    {features?.download ? (
                        features.download
                    ) : (
                        <TableToExcel
                            fileName={reportName}
                            sheetName=""
                            targetRef={printRef}
                            reportDetails={reportDetails}
                        />
                    )}

                    <Print
                        componentRef={printRef}
                        setPrintMode={setPrintMode}
                    />

                    <Tooltip title="Reload table">
                        <IconButton
                            size="small"
                            onClick={() => qc.resetQueries(queryKey)}
                        >
                            <ReplayOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <SelectColumns
                        columns={headersWithSelection}
                        selectedColumns={selectedHeaders}
                        setColumns={setSelectedHeaders}
                        storageKey={reportName}
                    />

                    {!printMode &&
                        isSelectionVisible &&
                        selectedRows.size > 0 && (
                            <MultiPermissionAuthorize
                                ops={[
                                    ...permissions.delete,
                                    "DELETE COLLECTION",
                                ]}
                                strategy="some"
                            >
                                <Tooltip
                                    title={`Delete ${selectedRows.size} selected ${itemTypeName}(s)`}
                                >
                                    <IconButton onClick={handleBulkDelete}>
                                        <DeleteOutlineOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {/* <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteOutlineOutlined />}
                                    onClick={handleBulkDelete}
                                    sx={{ mr: 1 }}
                                >
                                  
                                </Button> */}
                            </MultiPermissionAuthorize>
                        )}
                </Grid>

                <Grid item xs={12} md="auto">
                    {blindPagination ? (
                        <BlindPagination
                            pagination={pagination}
                            setPagination={setPagination}
                        />
                    ) : (
                        showPagination && (
                            <TablePagination
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                rowsPerPage={pagination?.limit}
                                component="div"
                                page={pagination?.page}
                                count={rowsCount && rowsCount}
                                rowsPerPageOptions={[
                                    100, 250, 500, 1000, 1500, 2000, 3000,
                                ]}
                            />
                        )
                    )}
                </Grid>

                {search && setSearch && (
                    <Grid item xs={12} md={3}>
                        <SearchBar filter={search} setFilter={setSearch} />
                    </Grid>
                )}
            </Grid>

            <Grid item xs={12}>
                {openDeletionFeedback && (
                    <Grid item xs={12}>
                        <Alert
                            severity={
                                delMutation?.status === "success"
                                    ? "success"
                                    : delMutation?.status === "error"
                                      ? "error"
                                      : "info"
                            }
                            sx={{ my: 2 }}
                        >
                            <Box
                                component="pre"
                                sx={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    fontFamily: "monospace",
                                    fontSize: "0.875rem",
                                    margin: 0,
                                }}
                            >
                                {deletionResponse}
                            </Box>
                        </Alert>
                    </Grid>
                )}
            </Grid>

            <Grid item xs={12} my={1}>
                {isLoading && <LinearProgress />}
            </Grid>

            <TableContainer
                component={Paper}
                variant="outlined"
                ref={printRef}
                sx={{
                    overflow: !printMode ? "auto" : undefined,
                    maxHeight: !printMode
                        ? { xs: "75vh", md: "calc(100vh - 175px)" }
                        : undefined,
                    px: printMode ? 4 : undefined,
                    whiteSpace: "nowrap",
                }}
            >
                <Table
                    padding="none"
                    sx={{
                        minWidth: !printMode ? "auto" : "100%",
                        whiteSpace: "nowrap",
                    }}
                    stickyHeader
                >
                    <TableHead style={{ whiteSpace: "nowrap" }}>
                        {printMode && (
                            <TableRow style={{ whiteSpace: "nowrap" }}>
                                <TableCell
                                    colSpan={dataHeaders.length + 1}
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    <Header
                                        name={
                                            selectedReportTemplate.name ||
                                            reportName
                                        }
                                        details={reportDetails}
                                    />
                                </TableCell>
                            </TableRow>
                        )}

                        <TableRow
                            sx={{
                                textTransform: "capitalize",
                                "& > *": {
                                    background: !printMode
                                        ? "linear-gradient(90deg, #EFC904 0%, #EFC904 40%, #EFC904 100%)"
                                        : "common.white",
                                    color: !printMode ? "#111110ff" : "inherit",
                                    fontWeight: "bold",
                                    fontSize: printMode ? "12px" : "0.8rem",
                                    whiteSpace: "nowrap",
                                    letterSpacing: !printMode
                                        ? "0.04em"
                                        : undefined,
                                    textShadow: !printMode
                                        ? "0 1px 2px rgba(0,0,0,0.25)"
                                        : undefined,
                                    position: !printMode ? "sticky" : undefined,
                                    top: !printMode ? 0 : undefined,
                                    zIndex: !printMode ? 10 : undefined,
                                    borderBottom: !printMode
                                        ? "2px solid #486C22"
                                        : undefined,
                                },
                            }}
                            style={{ whiteSpace: "nowrap" }}
                        >
                            {!printMode && isSelectionVisible && (
                                <TableCell
                                    style={{
                                        textAlign: "center",
                                        maxWidth: "6ch",
                                        whiteSpace: "nowrap",

                                        background: !printMode
                                            ? "linear-gradient(90deg, #EFC904 0%, #EFC904 40%, #EFC904 100%)"
                                            : "common.white",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "inline-block",

                                            borderRadius: "3px",
                                            padding: "2px",
                                        }}
                                    >
                                        <Checkbox
                                            checked={
                                                data?.length > 0 &&
                                                selectedRows.size ===
                                                    data.length
                                            }
                                            indeterminate={
                                                selectedRows.size > 0 &&
                                                selectedRows.size < data?.length
                                            }
                                            onChange={handleSelectAll}
                                            sx={{
                                                color: "#111110",
                                                "&.Mui-checked": {
                                                    color: "#486C22",
                                                },
                                                "&.MuiCheckbox-indeterminate": {
                                                    color: "#486C22",
                                                },
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                            )}
                            <TableCell
                                style={{
                                    textAlign: "center",
                                    maxWidth: "6ch",
                                    whiteSpace: "nowrap",
                                    color: !printMode ? "#111110ff" : "inherit",
                                }}
                            >
                                S.no
                            </TableCell>
                            {selectedHeaders && printMode
                                ? printables.map((col: any, idx: number) => (
                                      <TableCell
                                          key={idx}
                                          style={{
                                              padding: "0 16px",
                                              whiteSpace: "nowrap",
                                          }}
                                      >
                                          {typeof headers[col] === "string"
                                              ? headers[col]
                                              : (headers[col] as ComplexHeaders)
                                                    ?.label}
                                      </TableCell>
                                  ))
                                : dataHeaders.map((col: any, idx: number) => (
                                      <TableCell
                                          key={idx}
                                          style={{
                                              padding: "0 16px",
                                              whiteSpace: "nowrap",
                                          }}
                                      >
                                          {typeof headers[col] === "string"
                                              ? headers[col]
                                              : (headers[col] as ComplexHeaders)
                                                    ?.label}
                                      </TableCell>
                                  ))}

                            {!printMode && (
                                <MultiPermissionAuthorize
                                    ops={[
                                        ...permissions.edit,
                                        ...permissions.delete,
                                    ]}
                                    strategy="some"
                                >
                                    <TableCell
                                        style={{
                                            padding: "0 16px",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        actions
                                    </TableCell>
                                </MultiPermissionAuthorize>
                            )}
                        </TableRow>
                    </TableHead>

                    <TableBody
                        sx={{
                            "& *": printMode
                                ? {
                                      fontSize: "10px!important",
                                      whiteSpace: "nowrap",
                                  }
                                : {},
                            whiteSpace: "nowrap",
                        }}
                    >
                        {data &&
                            data.map((item: any, idx: number) => (
                                <Fragment key={idx}>
                                    <TableRow
                                        sx={
                                            !printMode
                                                ? {
                                                      bgcolor:
                                                          idx % 2 === 0
                                                              ? "inherit"
                                                              : "common.white",

                                                      "&:hover": {
                                                          bgcolor: "#486C22",
                                                      },
                                                  }
                                                : undefined
                                        }
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        {!printMode && isSelectionVisible && (
                                            <TableCell
                                                style={{
                                                    textAlign: "center",
                                                    maxWidth: "6ch",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "inline-block",

                                                        borderRadius: "3px",
                                                        padding: "2px",
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={selectedRows.has(
                                                            item[deletionProp]
                                                        )}
                                                        onChange={() =>
                                                            handleSelectRow(
                                                                item[
                                                                    deletionProp
                                                                ]
                                                            )
                                                        }
                                                        sx={{
                                                            color: "#111110",
                                                            "&.Mui-checked": {
                                                                color: "#486C22",
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </TableCell>
                                        )}
                                        <TableCell
                                            style={{
                                                textAlign: "center",
                                                maxWidth: "6ch",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {pagination.page > 0
                                                ? pagination.page *
                                                      pagination.limit +
                                                  idx +
                                                  1
                                                : idx + 1}
                                        </TableCell>

                                        {printMode
                                            ? printables.map(
                                                  (col: any, idx: number) => (
                                                      <TableCell
                                                          sx={{
                                                              px: 2,
                                                              py: printMode
                                                                  ? 0.5
                                                                  : undefined,
                                                              whiteSpace:
                                                                  "nowrap",
                                                          }}
                                                          key={idx}
                                                          width="fit-content"
                                                          style={{
                                                              ...(typeof headers[
                                                                  col
                                                              ] === "string"
                                                                  ? {}
                                                                  : headers[
                                                                        col
                                                                    ]?.styles(
                                                                        item
                                                                    )),
                                                          }}
                                                          {...(typeof headers[
                                                              col
                                                          ] === "string"
                                                              ? {}
                                                              : (headers[
                                                                    col
                                                                ]?.props(
                                                                    item
                                                                ) ?? {}))}
                                                      >
                                                          {item[col]}
                                                      </TableCell>
                                                  )
                                              )
                                            : dataHeaders.map(
                                                  (col: any, idx: number) => (
                                                      <TableCell
                                                          sx={{
                                                              px: 2,
                                                              py: printMode
                                                                  ? 0.5
                                                                  : undefined,
                                                              whiteSpace:
                                                                  "nowrap",
                                                          }}
                                                          key={idx}
                                                          width="fit-content"
                                                          style={{
                                                              ...(typeof headers[
                                                                  col
                                                              ] === "string"
                                                                  ? {}
                                                                  : headers[
                                                                        col
                                                                    ]?.styles(
                                                                        item
                                                                    )),
                                                          }}
                                                          {...(typeof headers[
                                                              col
                                                          ] === "string"
                                                              ? {}
                                                              : (headers[
                                                                    col
                                                                ]?.props(
                                                                    item
                                                                ) ?? {}))}
                                                      >
                                                          {item[col]}
                                                      </TableCell>
                                                  )
                                              )}

                                        {!printMode && (
                                            <MultiPermissionAuthorize
                                                ops={[
                                                    ...permissions.edit,
                                                    ...permissions.delete,
                                                ]}
                                                strategy="some"
                                            >
                                                <TableCell
                                                    sx={{
                                                        px: 2,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    <MultiPermissionAuthorize
                                                        ops={permissions.edit}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleEdit(item)
                                                            }
                                                        >
                                                            <EditOutlined fontSize="small" />
                                                        </IconButton>
                                                    </MultiPermissionAuthorize>

                                                    <MultiPermissionAuthorize
                                                        ops={permissions.delete}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    item[
                                                                        deletionProp
                                                                    ]
                                                                )
                                                            }
                                                        >
                                                            <DeleteOutlineOutlined fontSize="small" />
                                                        </IconButton>
                                                    </MultiPermissionAuthorize>
                                                </TableCell>
                                            </MultiPermissionAuthorize>
                                        )}
                                    </TableRow>
                                </Fragment>
                            ))}

                        {summaryRow && data && (
                            <TableRow sx={{ bgcolor: "grey.400" }}>
                                {summaryRow(dataHeaders).map((summary, idx) => (
                                    <TableCell
                                        key={idx}
                                        sx={{
                                            px: 2,
                                            py: 0.5,
                                            fontWeight: "bold",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {summary}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )}
                    </TableBody>

                    {printMode && <Footer span={dataHeaders.length + 1} />}
                </Table>
            </TableContainer>

            <Dialog open={open}>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will irreversibly delete the item, continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setOpen(false)}
                    >
                        close
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                            next();
                            setOpen(false);
                        }}
                    >
                        delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openBulkDeleteDialog}>
                <DialogTitle>Confirm Bulk Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        You are about to delete {selectedRows.size}{" "}
                        {itemTypeName}(s). This action cannot be undone.
                    </DialogContentText>
                    <DialogContentText sx={{ mb: 2, fontWeight: "bold" }}>
                        Please type "delete all" to confirm:
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        variant="outlined"
                        placeholder="Type 'delete all' to confirm"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        error={
                            confirmationText !== "" &&
                            confirmationText.toLowerCase() !== "delete all"
                        }
                        helperText={
                            confirmationText !== "" &&
                            confirmationText.toLowerCase() !== "delete all"
                                ? "Please type 'delete all' exactly"
                                : ""
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            setOpenBulkDeleteDialog(false);
                            setConfirmationText("");
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={confirmBulkDelete}
                        disabled={
                            confirmationText.toLowerCase() !== "delete all"
                        }
                    >
                        Delete All
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    ) : null;
};

export default BaseTable;

type SelectColumnsProps = {
    columns: { [key: string]: string | { label: string } };
    selectedColumns: string[];
    setColumns: Dispatch<SetStateAction<string[]>>;
    icon?: ReactElement;
    label?: string;
    storageKey?: any;
};

export const SelectColumns = ({
    columns,
    selectedColumns,
    setColumns,
    icon,
    label,
    storageKey,
}: SelectColumnsProps) => {
    const [anchor, setAnchor] = useState<Element | null>(null);

    const validColumns = useMemo(
        () => new Set(Object.keys(columns)),
        [columns]
    );

    // Initialize selected state
    const [selected, setSelected] = useState<{ [key: string]: boolean }>(() => {
        let base: Record<string, boolean> = {};

        selectedColumns.forEach((k) => {
            if (validColumns.has(k)) {
                base[k] = true;
            }
        });

        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                const filtered = Object.fromEntries(
                    Object.entries(parsed).filter(
                        ([k, v]) =>
                            validColumns.has(k) && typeof v === "boolean"
                    )
                ) as Record<string, boolean>;
                base = { ...base, ...filtered };
            }
        }

        return base;
    });

    const open = useMemo(() => Boolean(anchor), [anchor]);

    const handleChange = (checked: boolean, col: string) => {
        const targetSelected = { ...selected, [col]: checked };
        setSelected(targetSelected);

        // Save to localStorage
        if (storageKey)
            localStorage.setItem(storageKey, JSON.stringify(targetSelected));

        // Update parent
        const targetCols = Object.entries(targetSelected)
            .filter(([_, v]) => v)
            .map(([k]) => k);
        setColumns(targetCols);
    };

    // Sync with parent only on mount (optional)
    useEffect(() => {
        const cols = Object.entries(selected)
            .filter(([k, v]) => v && validColumns.has(k))
            .map(([k]) => k);
        setColumns(cols);
    }, []); // empty dependency → run only once

    return (
        <>
            <Tooltip title={label ?? "Select Columns"}>
                <IconButton
                    size="small"
                    onClick={(e) => setAnchor(e.currentTarget)}
                >
                    {icon ?? <ViewColumn fontSize="small" />}
                </IconButton>
            </Tooltip>

            <Menu
                open={open}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                color="primary"
            >
                {Object.entries(columns).map(([k, v]: any) => (
                    <MenuItem key={k} dense>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selected[k] ?? false}
                                    onChange={(e, checked) =>
                                        handleChange(checked, k)
                                    }
                                />
                            }
                            label={typeof v === "string" ? v : v.label}
                            value={k}
                            style={{ width: "100%" }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
